import { CarInsuranceService } from '@wz-services';
import { dateFromISOString } from '@wz-utilities/date.utilities';
import { roundNumber } from '@wz-utilities/math.utilities';
import { cleanNullProperties } from '@wz-utilities/object.utilities';
import { VehicleApiModels } from 'models/api/vehicle-api.models';
import { PolicyModels } from 'models/policy.models';
import { QuotationApiModels } from '../../../../models/api/quotation-api.models';
import { AutomobileRiskModels } from '../../../../models/automobile-risk.models';
import { PaymentModels } from '../../../../models/payment.models';
import { QuotationModels } from '../../../../models/quotation.models';
import { DateProjector } from './date-projector';

export function projectQuotationPaymentCredentials(
  payment: QuotationModels.QuotationPaymentCredentials,
): QuotationModels.QuotationPaymentCredentials {
  return Object.assign({}, payment, {
    birthDate: DateProjector.fromAppToApi(payment.birthDate),
  });
}

export function projectAnonymousQuotationSubscriptionForm(
  form: QuotationModels.SubscriptionForm,
  payment: PaymentModels.PaymentRequest,
  birthDate: string,
): QuotationApiModels.AnonymousSubscriptionData {
  let data = {
    dateEffet: DateProjector.fromAppToApi(form.infos.dateEffet),
    quittancement: form.quittancement,
    quantieme: form.infos.quantieme,
    payment: payment,
  } as QuotationApiModels.AnonymousSubscriptionData;
  data.birthDate = DateProjector.fromAppToApi(birthDate)!;
  return data;
}

export function projectQuotationTableRow(
  apiModel: QuotationApiModels.QuotationTableRow,
): QuotationModels.QuotationTableRow {
  return Object.assign({}, apiModel, {
    creationDate: dateFromISOString(apiModel.creationDate),
    signatureReadyDate: dateFromISOString(apiModel.signatureReadyDate!),
    isArchiving: false,
  });
}

export const projectVehicleFromVehicleSearch = (
  search: VehicleApiModels.VehicleDetails,
): PolicyModels.VehicleDetails => {
  if (!search) {
    return {};
  }

  return {
    carrosserie: search.carrosserie,
    chevauxFiscaux: search.chevauxFiscaux,
    dateAchatVehicule: dateFromISOString(search.dateAchatVehicule!)!,
    dateMiseEnCirculation: dateFromISOString(search.dateMiseEnCirculation!)!,
    energie: search.energie,
    marque: search.marque,
    modele: search.modele,
    codeGta: search.codeGta,
    typeMine: search.typeMine,
    version: search.version,
  };
};

export function projectQuotationSummary(
  apiModel: QuotationApiModels.QuotationResponse,
): QuotationModels.QuotationSummary {
  const {
    quotationRequest,
    pricingInfos,
    contractStartDate,
    staggering,
    vehicle,
    quantieme,
  } = apiModel.data;

  const risk = projectAutomobileRiskResponse(quotationRequest.risk);
  const quotSummary: QuotationModels.QuotationSummary | any = {
    status: apiModel.status,
    lastPaymentStatus: apiModel.lastPaymentStatus,
    subscriptionInfo: {
      infos: {
        dateEffet: contractStartDate
          ? DateProjector.fromApiToApp(contractStartDate)
          : '',
        quantieme: quantieme ? quantieme : 5,
      },
      quittancement: staggering,
    },
    brokerConsultingFileUrl: apiModel.brokerConsultingSheetFileUrl,
    quotationFileUrl: apiModel.quotationFileUrl,
    cgFileUrl: apiModel.cgFileUrl,
    ipidFileUrl: apiModel.ipidFileUrl,
    risk: risk,
    details: projectQuotationDataDetailsResponse(
      pricingInfos,
      quotationRequest,
    ),
    fees: pricingInfos.fees,
    toPayMonthly: pricingInfos.pricing.monthly.firstClientPayment,
    toPayYearly: pricingInfos.pricing.yearly.firstClientPayment,
    nextToPayMonthly: pricingInfos.pricing.monthly.clientTotalPrice,
    nextToPayYearly: pricingInfos.pricing.yearly.clientTotalPrice,
    monthsForFirstPayment: pricingInfos.pricingRate.monthsForFirstPayment,
    vehicle: projectVehicleFromVehicleSearch(vehicle!),
    ria: apiModel.data.quotationRequest.risk.ria,
  };

  return quotSummary;
}

export function projectQuotationDataDetailsResponse(
  pricingInfos: QuotationModels.PricingInfosResponse,
  quotationRequest: QuotationApiModels.AutomobileQuotationRequest,
): QuotationModels.QuotationDetails {
  return {
    commission: toFlatPercent(
      pricingInfos.pricingRate.brokerCommissionRate +
        pricingInfos.pricingRate.brokerLoadingRate,
    ),
    fees: quotationRequest.brokerFees!,
    formula: quotationRequest.formula!,
    formulaDetails: {
      optionnalWarranties: quotationRequest?.optionalWarranties?.map((ow) => {
        return {
          selected: true,
          type: ow,
        };
      })!,
    },
  };
}

export function projectFranchiseEnumToWarrantyEnum(
  franchise: QuotationModels.FranchiseEnum,
): QuotationModels.WarrantyEnum | null {
  switch (franchise) {
    case QuotationModels.FranchiseEnum.BrisDeGlace:
      return QuotationModels.WarrantyEnum.BrisDeGlace;
    case QuotationModels.FranchiseEnum.DommageToutAccident:
      return QuotationModels.WarrantyEnum.DommageTousAccidents;
    case QuotationModels.FranchiseEnum.IncendieVol:
      return QuotationModels.WarrantyEnum.IncendieVol;
    default:
      return null;
  }
}

export function projectCarPricingResponse(
  apiPricingResponse: QuotationApiModels.PricingResponse,
): QuotationModels.FullFormulaPricingInformations {
  const franchises: { [wEnum: number]: number } = Object.keys(
    apiPricingResponse.franchises,
  ).reduce((obj: any, key) => {
    const warrEnum = projectFranchiseEnumToWarrantyEnum(+key);
    if (warrEnum) {
      obj[warrEnum] = apiPricingResponse.franchises[key as any];
    }
    return obj;
  }, {});

  return {
    franchises: franchises,
    formula: {
      broker: apiPricingResponse.brokerCommissions,
      client: apiPricingResponse.clientPrice,
      fees: apiPricingResponse.fees,
    },
    optionalWarranties: apiPricingResponse.optionalWarrantyPrices,
  };
}

export function projectCarPricingWithMetadataResponse(
  apiCarPricing: QuotationApiModels.CarPricingWithMetadataResponse,
): QuotationModels.BrokerPricingData {
  const { quotationRequest, pricing } = apiCarPricing;
  const formulas: { [key: number]: QuotationModels.Formula } = {};

  apiCarPricing.warrantySettings.forEach((warrantySetting) => {
    let formula: QuotationModels.Formula = formulas[warrantySetting.formula];
    if (!formula) {
      formula = {
        optionalWarranties: [],
        type: warrantySetting.formula,
        pricing: {
          broker: undefined, // TODO !!!
          client: undefined, // TODO !!!
        },
        warranties: [],
      };

      formulas[warrantySetting.formula] = formula;
    }

    if (warrantySetting.isOption) {
      formula.optionalWarranties.push({
        pricing: pricing.optionalWarrantyPrices[warrantySetting.warranty],
        warranty: warrantySetting.warranty,
        selected: false,
      });
    } else {
      formula.warranties.push({
        warranty: warrantySetting.warranty,
        isAvailable: true,
      });
    }
  });

  return {
    currentFormula: formulas[quotationRequest.formula],
    currentManagement: quotationRequest.brokerFees,
    currentPackageId: quotationRequest.packageId,
    pricing: {
      broker: pricing.brokerCommissions,
      client: pricing.clientPrice,
    },
    currentCommission: toFlatPercent(
      apiCarPricing.maxCommission + quotationRequest.brokerLoading,
    ),
    metadatas: {
      commission: {
        min: toFlatPercent(apiCarPricing.maxCommission),
        max: toFlatPercent(
          apiCarPricing.maxLoading + apiCarPricing.maxCommission,
        ),
      },
      hasFixedBrokerData: apiCarPricing.hasFixedBrokerData,
      formulas: Object.values(formulas),
      management: {
        min: apiCarPricing.minFees,
        max: apiCarPricing.maxFees,
      },
    },
  };
}

function toFlatPercent(value: number) {
  return parseFloat(value.toFixed(2)) * 100;
}

export function projectCreateQuotationRequest(
  automobileRisk: CarInsuranceService,
  isModeB2C: boolean,
  sendEmail: boolean,
  packageId?: number,
  formula?: QuotationModels.FormulaEnum,
  commission?: number,
  brokerFees?: number,
  details?: QuotationModels.FormulaDetails,
): QuotationApiModels.CreateQuotationRequest {
  return {
    quotationRequest: projectAutomobileQuotationRequest(
      automobileRisk.riskFormData,
      automobileRisk.quotationId!,
      packageId,
      formula,
      commission,
      brokerFees,
      details,
    ),
    isModeB2C: isModeB2C,
    hasToSendQuotationToClient: sendEmail,
  };
}

export function projectAutomobileQuotationRequest(
  automobileRisk: AutomobileRiskModels.Form,
  quotationId?: string,
  packageId?: number,
  formula?: QuotationModels.FormulaEnum,
  commission?: number,
  brokerFees?: number,
  details?: QuotationModels.FormulaDetails,
): QuotationApiModels.AutomobileQuotationRequest {
  const risk = projectAutomobileRiskRequest(automobileRisk);
  return {
    risk: risk!,
    formula: formula,
    packageId: packageId,
    brokerLoading: commission
      ? roundNumber((commission - 10) / 100)
      : undefined,
    brokerFees: brokerFees,
    optionalWarranties: details
      ? details.optionnalWarranties
          .filter((ow) => ow.selected)
          .map((ow) => ow.type)
      : [],
    quotationId: quotationId!,
  };
}

export function projectPolicyAutomobileRiskResponse(
  apiPolicyRisk: QuotationApiModels.PolicyAutomobileRisk,
) {
  const subscriptionInfo: any = {
    dateEffet: DateProjector.fromApiToApp(apiPolicyRisk.dateEffet),
  };

  return projectAutomobileRiskResponse(apiPolicyRisk.risk, subscriptionInfo);
}

export function projectPolicyAutomobileRiskRequest(
  risk: AutomobileRiskModels.Form,
): QuotationApiModels.PolicyAutomobileRisk {
  return {
    risk: projectAutomobileRiskRequest(risk)!,
    dateEffet: DateProjector.fromAppToApi(risk.subscriptionInfo!.dateEffet)!,
  };
}

export function projectAutomobileRiskResponse(
  apiRisk: QuotationApiModels.AutomobileRisk,
  subscriptionInfo?: QuotationModels.SubscriptionInfo,
): any {
  const apiConducteur = apiRisk.conducteurs?.[0];
  if (!apiConducteur) {
    return { assureInfos: apiRisk.assure };
  }

  const apiAntecedents = apiConducteur.antecedentAssurance;
  const apiAlcoolemie = apiConducteur.antecedent?.alcoolemie;
  const antecedentsAssurance: AutomobileRiskModels.AntecedantsAssurance | any =
    {
      alcoolemie: apiConducteur?.antecedent!.alcoolemie
        ? {
            alcoolique: true,
            dateDernierControle: DateProjector.fromApiToApp(
              apiAlcoolemie?.dateDernierControleAlcoolemie!,
            ),
            type: apiAlcoolemie?.typeMesureAlcoolemie,
            nombre: apiAlcoolemie?.nombreControleAlcoolemiePositif,
            mesureAir: apiAlcoolemie?.mesureAlcoolemieExpire,
            mesureSang: apiAlcoolemie?.mesureAlcoolemieParLitreSang,
          }
        : {
            alcoolique: false,
          },
      bonus: apiConducteur.bonus!,
      nombreAnneeAssurance: apiAntecedents?.nombreAnneeAssurance!,
      nombreAnneeBonus50: apiAntecedents?.nombreAnneeBonus50 || 0,
      periode36Mois: apiAntecedents?.periodeAssurance!,
      periodeInterruption: apiAntecedents?.interruptionAssuranceMaintenant!,
      raisonResiliation: apiAntecedents?.raisonResiliation!,
      condamnations: apiAntecedents?.condamnations
        ? {
            type: apiAntecedents.condamnations.type,
            hasRecidive: apiAntecedents.condamnations.hasRecidive,
            hasCondamnation: apiAntecedents.condamnations.hasCondamnation,
          }
        : {
            hasCondamnation: false,
            hasRecidive: false,
            type: [],
          },
    };

  const conducteurPrincipal: AutomobileRiskModels.Conducteur | any = {
    dateNaissance: DateProjector.fromApiToApp(apiConducteur.dateNaissance!),
    datePermis: DateProjector.fromApiToApp(apiConducteur.datePermis!),
    nombreEnfants: apiConducteur.nombreEnfants!,
    profession: apiConducteur.profession!,
    sanctionPermis: {
      sanction: apiConducteur.antecedent?.sanctionPermis!,
      raison: apiConducteur.antecedent?.raisonSanctionPermis!,
      epoque: apiConducteur.antecedent?.sanctionPermisEpoque!,
      duree: apiConducteur.antecedent?.suspensionPeriode!,
    },
    sinistres: apiConducteur.antecedent?.sinistres.map((s) => ({
      date: DateProjector.fromApiToApp(s.date),
      type: s.type,
      nature: s.nature,
      responsabilite: s.responsabilite,
      publicId: s.publicId,
    }))!,
    situationFamiliale: apiConducteur.situationFamiliale!,
    typePermis: apiConducteur.typePermis!,
  };

  const res = cleanNullProperties({
    car: {
      codeGta: apiRisk.codeGta ?? '',
      dateAchatVehicule: DateProjector.fromApiToApp(apiRisk.dateAchat!),
      dateMiseEnCirculation: DateProjector.fromApiToApp(
        apiRisk.dateMiseEnCirculation!,
      ),
      immatriculation: apiRisk.assure.immatriculation!,
    },
    infos: {
      codeInseePerso: {
        codeInsee: apiRisk.codeInseeParkingNuit!,
        codePostal: apiRisk.codePostalPerso!,
      },
      codeInseePro: apiRisk.codePostalPro
        ? {
            codeInsee: apiRisk.codeInseeParkingJour,
            codePostal: apiRisk.codePostalPro,
          }
        : null,
      financement: apiRisk.financement,
      vehiculeDejaAssure: apiRisk.vehiculeEstDejaAssure,
      usage: apiRisk.usage,
      titulaireCarteGrise: apiRisk.titulaireCarteGrise,
      nombreKmAnnuel: apiRisk.nombreKmAnnuel,
      garage: apiRisk.garage,
    },
    assureInfos: apiRisk.assure,
    conducteur: conducteurPrincipal,
    antecedantsAssurance: antecedentsAssurance,
    subscriptionInfo: subscriptionInfo,
  });

  return res;
}

export function projectAutomobileRiskRequest(
  automobileRisk: AutomobileRiskModels.Form,
): QuotationApiModels.AutomobileRisk | null {
  if (!automobileRisk) {
    return null;
  }
  const { infos, conducteur, antecedantsAssurance, car, immatriculation } =
    automobileRisk;

  if (!car) {
    return { assure: { ...automobileRisk.assureInfos } };
  }

  const sanctionPermis = conducteur?.sanctionPermis;

  const antecedentApi: QuotationApiModels.Antecedent = {
    alcoolemie: antecedantsAssurance!.alcoolemie.alcoolique
      ? {
          dateDernierControleAlcoolemie: DateProjector.fromAppToApi(
            antecedantsAssurance!.alcoolemie.dateDernierControle,
          ),
          typeMesureAlcoolemie: antecedantsAssurance!.alcoolemie.type,
          nombreControleAlcoolemiePositif:
            antecedantsAssurance!.alcoolemie.nombre,
          mesureAlcoolemieExpire:
            antecedantsAssurance!.alcoolemie.type ===
            QuotationModels.TypeMesureAlcoolemieEnum.Ethylotest
              ? antecedantsAssurance!.alcoolemie.mesureAir
              : undefined,
          mesureAlcoolemieParLitreSang:
            antecedantsAssurance!.alcoolemie.type ===
            QuotationModels.TypeMesureAlcoolemieEnum.PriseDeSang
              ? antecedantsAssurance!.alcoolemie.mesureSang
              : undefined,
        }
      : undefined,
    sanctionPermis: sanctionPermis?.sanction!,
    sinistres: conducteur!.sinistres.map((s) => ({
      date: DateProjector.fromAppToApi(s.date),
      type: s.type,
      nature: s.nature,
      responsabilite: s.responsabilite,
      publicId: s.publicId,
    })) as any,
  };

  if (
    sanctionPermis?.sanction !==
    QuotationModels.SuspensionAnnulationPermisEnum.None
  ) {
    antecedentApi.sanctionPermisEpoque = sanctionPermis?.epoque;
    antecedentApi.raisonSanctionPermis = sanctionPermis?.raison;

    if (
      sanctionPermis?.sanction ===
      QuotationModels.SuspensionAnnulationPermisEnum.Suspendu
    ) {
      antecedentApi.suspensionPeriode = sanctionPermis?.duree;
    }
  }

  const antecedentAssuranceApi: QuotationApiModels.AntecedentAssurance = {
    periodeAssurance: antecedantsAssurance!.periode36Mois,
    nombreAnneeAssurance: antecedantsAssurance!.nombreAnneeAssurance,
    interruptionAssuranceMaintenant: antecedantsAssurance!.periodeInterruption,
    raisonResiliation: antecedantsAssurance!.raisonResiliation,
    nombreAnneeBonus50: antecedantsAssurance!.nombreAnneeBonus50 || 0,
    condamnations: antecedantsAssurance!.condamnations,
    isResil36: false, //antecedantsAssurance.isResil36,
    lastSinisterJokerDate: '', //antecedantsAssurance.lastSinisterJokerDate
  };

  const conducteurApi: QuotationApiModels.Conducteur = {
    antecedent: antecedentApi,
    antecedentAssurance: antecedentAssuranceApi,
    dateNaissance: DateProjector.fromAppToApi(conducteur?.dateNaissance)!,
    datePermis: DateProjector.fromAppToApi(conducteur?.datePermis)!,
    bonus: antecedantsAssurance?.bonus,
    typePermis: conducteur?.typePermis,
    isPrincipal: true,
    nombreEnfants: conducteur?.nombreEnfants,
    profession: conducteur?.profession,
    situationFamiliale: conducteur?.situationFamiliale,
  };

  const codeInseePro = automobileRisk.infos?.codeInseePro;
  const codeInseePerso = automobileRisk.infos?.codeInseePerso;

  return {
    codeGta: car.codeGta,
    dateAchat: DateProjector.fromAppToApi(car.dateAchatVehicule)!,
    dateMiseEnCirculation: DateProjector.fromAppToApi(
      car.dateMiseEnCirculation,
    )!,
    codePostalPerso: codeInseePerso?.codePostal ?? '',
    codePostalPro:
      codeInseePro && codeInseePro.codeInsee ? codeInseePro.codePostal : '',
    codeInseeParkingJour:
      codeInseePro && codeInseePro.codeInsee // jour can be pro
        ? codeInseePro.codeInsee
        : (codeInseePerso?.codeInsee ?? ''),
    codeInseeParkingNuit: codeInseePerso?.codeInsee ?? '', // perso always nuit
    conducteurs: [conducteurApi],
    financement: infos!.financement,
    garage: infos!.garage,
    nombreKmAnnuel: infos!.nombreKmAnnuel,
    vehiculeEstDejaAssure: infos!.vehiculeDejaAssure,
    titulaireCarteGrise: infos!.titulaireCarteGrise,
    usage: infos!.usage,
    assure: {
      ...automobileRisk.assureInfos,
      immatriculation: immatriculation || car.immatriculation,
    },
  };
}
